import store from "@/store";
import axios from "axios";
import { limitConcurrency } from "@/libs/concurrency";

const MAX_CONCURRENT_REQUESTS = 2;
// longer server messages are shortened in the error snackbar
const MAX_ERROR_DETAIL_LENGTH = 200;

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

axios.interceptors.request.use(
  function (config) {
    if (!config.url.includes("countermetrics.org")) {
      // we do not want to send the custom header to countermetrics.org
      // because is messes up the CORS headers
      config.headers["celus-version"] = store.getters.celusVersion;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  function (response) {
    // Clear the version diaolog when no error is returned
    store.dispatch("setNewCelusVersion", { new_version: null });
    // Do something with response data
    return response;
  },
  function (error) {
    // Do something with response error
    if (axios.isCancel(error)) {
      // we ignore this
    } else if (error.response?.status === 401) {
      // if there is 401 error, try to (re)authenticate
      // this can either happen when using shibboleth and the session expires
      // or when using normal login and the session expires
      //
      // in both cases, we only do it if the request was sent to the same origin
      if (
        error.response?.request?.responseURL &&
        error.response?.request?.responseURL.startsWith(window.location.origin)
      ) {
        store.dispatch("cleanUserData");
        store.dispatch("setShowLoginDialog", { show: true });
      }
    } else if (
      error.response?.status === 403 &&
      !!error.response.headers["celus-otp-missing"]
    ) {
      // reload user data where OTP should be enforced
      store.dispatch("cleanUserData");
      store.dispatch("loadUserData");
    } else if (
      error.response?.status === 409 &&
      error.response.headers["celus-version"] !== store.getters.celusVersion
    ) {
      // Display new celus version dialog
      store.dispatch("setNewCelusVersion", {
        new_version: error.response.headers["celus-version"],
      });
    } else if (typeof error.response === "undefined") {
      // this may be any of different errors:
      //  - CORS issue (when accessing the CDN or sentry)
      //  - network issue
      //  - CORS issue caused by EduID session expiration and silent redirect to eduid.cz
      //
      // Because the EduID issue is best handled by properly configuring the server
      // (allowing assets to be accessed without authentication, using 401 in /api responses)
      // we assume EduID does not need to be handled here and just log the error
      console.error(
        "Unknown error (CORS, network, etc.) - ignoring",
        error.config?.url,
      );
    }
    return Promise.reject(error);
  },
);

limitConcurrency(axios, MAX_CONCURRENT_REQUESTS);

axios.interceptors.request.use(async (config) => {
  // only let requests marked as privileged unless state.letAxiosThrough is true
  // this helps to let requests wait until all the required setup is done (user is logged
  // in, some basic data is loaded, etc.)
  if (
    (store.state.user !== null && store.state.organizations !== null) ||
    config.privileged
  ) {
    return config;
  }
  const watcher = new Promise((resolve) => {
    console.debug(`delaying request for ${config.url}`);
    store.watch(
      (state, getters) => getters.letAxiosThrough,
      (newVal) => {
        if (newVal) resolve();
      },
    );
  });
  try {
    await watcher;
    return config;
  } catch (e) {
    console.error("error waiting for permission to use axios", e);
  }
});

// methods whose ungrouped requests are not cancelled when their component unmounts
const isMutation = (method) =>
  !["get", "head", "options"].includes((method || "get").toLowerCase());

const http_config = (args) => {
  const {
    component,
    group,
    label,
    errorMessage,
    errorTexts,
    dontShowError,
    ...config
  } = args;
  const grp = group || "";

  // a caller-provided signal takes over cancellation; ungrouped changes of data
  // finish even when the user leaves the page
  if (config.signal || !component || (!grp && isMutation(config.method))) {
    return config;
  }

  const controllers = store.state.cancellation.controllers[component];
  if (!controllers) {
    // the component lacks the cancellation mixin, or still makes requests well after
    // unmounting (e.g. a polling timer that was not cleared)
    console.warn(
      `http: ${component} is not registered for cancellation or was unmounted, request to ${config.url} cancelled`,
    );
    throw new axios.CanceledError(null, config);
  }
  if (store.state.cancellation.ongoing[component]) {
    throw new axios.CanceledError(null, config);
  }

  if (grp) {
    // a new request in a group supersedes the previous one
    controllers[grp]?.abort();
    store.dispatch("cancellation/set", { component, group: grp });
  }
  config.signal = controllers[grp].signal;
  return config;
};

// a message the server meant for the user, if the response has one
const serverMessage = (e) => {
  const data = e.response?.data;
  if (typeof data === "string") {
    return data.trimStart().startsWith("<") ? null : data.trim() || null;
  }
  for (const message of [
    data?.detail,
    data?.error,
    data?.non_field_errors?.[0],
  ]) {
    if (typeof message === "string" && message) return message;
  }
  return null;
};

const errorVerb = (method) => {
  if (!isMutation(method)) return "loading";
  return method.toLowerCase() === "delete" ? "deleting" : "saving";
};

const ellipsize = (text, maxLength) =>
  text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;

// e.g. "Error loading platforms (HTTP 403): You do not have permission…"
const errorText = (method, label, errorMessage, status, e) => {
  const text = errorMessage || `Error ${errorVerb(method)} ${label || "data"}`;
  const code = status ? ` (HTTP ${status})` : "";
  const detail = serverMessage(e);
  const suffix = detail
    ? `: ${ellipsize(detail, MAX_ERROR_DETAIL_LENGTH)}`
    : "";
  return `${text.replace(/[.:]\s*$/, "")}${code}${suffix}`;
};

const http_error = (args, e) => {
  if (axios.isCancel(e)) {
    return { response: null, error: null, cancelled: true };
  }

  const { label, errorMessage, dontShowError, errorTexts } = args;
  const status = e.response?.status;
  const error = (status === 400 && e.response.data?.error) || e;
  if (!dontShowError) {
    if (status === 403 && e.response.headers["celus-otp-missing"]) {
      console.debug("Supressing error response for missing OTP validation");
    } else if (status && errorTexts?.[status]) {
      store.dispatch("showError", { message: errorTexts[status] });
    } else {
      store.dispatch("showError", {
        message: errorText(args.method, label, errorMessage, status, e),
      });
    }
  }
  return { response: null, error, cancelled: false };
};

/**
 * Axios wrapper that cancels requests per component and shows errors.
 *
 * The returned promise always resolves, to `{ response, error, cancelled }`:
 * - success: `response` is the axios response;
 * - failure: `response` is null, `error` is the error, and a snackbar was shown
 *   unless `dontShowError`; callers should not show another one;
 * - cancellation: `response` and `error` are null and `cancelled` is true. Return
 *   right away without touching any state: whoever cancelled the request (a newer
 *   request in the same group, `abortGroup` or the unmount) is in charge of it.
 *
 * With `component` (added by the cancellation mixin and `useHttp`):
 * - a request with a `group` cancels the component's previous request in that group;
 * - unmounting the component cancels all its requests, except ungrouped POST, PUT,
 *   PATCH and DELETE requests, which are left to finish.
 *
 * The error snackbar is "Error loading <label>" ("saving" / "deleting" for requests
 * that change data) or `errorMessage`, then the HTTP status and the server's message
 * (`detail`, `error`, `non_field_errors` or a plain text body, shortened) if any.
 *
 * @param {Object} args - axios config plus the options below
 * @param {string} [args.label] - what is loaded or saved, for "Error loading <label>"
 * @param {string} [args.errorMessage] - error message used instead of the `label` one
 * @param {Object} [args.errorTexts] - error messages by HTTP status code
 * @param {boolean} [args.dontShowError] - do not show a snackbar on error
 * @param {string} [args.component] - component identifier for cancellation
 * @param {string} [args.group] - group of mutually exclusive requests of the component
 * @returns {Promise<{response: *, error: *, cancelled: boolean}>}
 */
const http = async (args) => {
  try {
    return {
      response: await axios(http_config(args)),
      error: null,
      cancelled: false,
    };
  } catch (e) {
    return http_error(args, e);
  }
};

/**
 * Like `http`, but saves the response as a file named by its Content-Disposition.
 */
const http_download = async (args) => {
  try {
    const axios_config = http_config(args);
    axios_config.responseType = "blob";
    const response = await axios(axios_config);

    // extract filename
    const fileName = response.headers["content-disposition"]
      .split("filename=")[1]
      .replaceAll('"', "");

    // prepare download link and click it
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    // cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return {
      response,
      error: null,
      cancelled: false,
    };
  } catch (e) {
    return http_error(args, e);
  }
};

export { http, http_download };
